import {
  Badge,
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@nexus/ui";
import type { MarketplaceFulfillmentStatus } from "@nexus/contracts";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { OrderLineVideoLabelEditor } from "@/features/marketplace/components/OrderLineVideoLabelEditor";
import { ScriptReviewAction } from "@/features/marketplace/components/ScriptReviewAction";
import {
  StatusMenu,
  StatusPill,
} from "@/features/marketplace/components/orders-list/StatusMenu";
import { fulfillmentOptionsForOpsAdminScript } from "@/features/marketplace/lib/orders-list-model";
import { opsVideoStatusMenuValue } from "@/features/marketplace/lib/ops-script-status-menu";
import {
  isOrderLineVideoSlotEmpty,
  orderLineVideoDisplayName,
  orderLineVideoKey,
} from "@/features/marketplace/lib/order-line-videos";
import type {
  MarketplaceOrder,
  OrderLineVideo,
  OrderScriptRow,
} from "@/features/marketplace/types/marketplace.types";
import {
  useDeleteAdminOrderScriptVideo,
  useUpdateOrderScriptVideoLabel,
} from "@/lib/queries/marketplace.queries";
import { useUpdateOrderScriptFulfillment } from "@/lib/queries";

import {
  ADMIN_ORDERS_CELL_ACTIONS_CLASS,
  ADMIN_ORDERS_CELL_DUE_CLASS,
  ADMIN_ORDERS_CELL_PARTNER_CLASS,
  ADMIN_ORDERS_CELL_SCRIPTS_CLASS,
  ADMIN_ORDERS_CELL_SENT_CLASS,
  ADMIN_ORDERS_CELL_STATUS_CLASS,
  ADMIN_ORDERS_CELL_TYPE_CLASS,
  ADMIN_ORDERS_GRID_CLASS,
  ADMIN_ORDERS_ROW_PADDING_CLASS,
} from "./admin-orders-grid";

type VideoDeleteDisposition = "remove_video" | "cancel_video";

interface AdminOrderVideoRowProps {
  order: Pick<MarketplaceOrder, "id" | "source" | "tenantId">;
  script: OrderScriptRow;
  video: OrderLineVideo;
  videos: readonly OrderLineVideo[];
  scriptTitle: string;
  rowIndex: number;
  canEditStatus: boolean;
  pending: boolean;
  canDelete: boolean;
  /** The Status filter kept this video because its status matched. */
  matchesStatusFilter?: boolean;
  onStatusChange: (next: MarketplaceFulfillmentStatus) => void;
}

export function AdminOrderVideoRow({
  order,
  script,
  video,
  videos,
  scriptTitle,
  rowIndex,
  canEditStatus,
  pending,
  canDelete,
  matchesStatusFilter = false,
  onStatusChange,
}: AdminOrderVideoRowProps) {
  const { t } = useTranslation();
  const updateLabel = useUpdateOrderScriptVideoLabel();
  const deleteVideo = useDeleteAdminOrderScriptVideo();
  const cancelVideo = useUpdateOrderScriptFulfillment();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const label = orderLineVideoDisplayName(video, t);
  const videoKey = orderLineVideoKey(video);
  const statusMenuValue = opsVideoStatusMenuValue(script, video);
  const statusOptions = fulfillmentOptionsForOpsAdminScript(
    order,
    script,
    videoKey,
  );
  const canCancelVideo =
    script.fulfillmentStatus !== "CANCELLED" &&
    script.fulfillmentStatus !== "ORDER_DELIVERED" &&
    script.fulfillmentStatus !== "COMPLETED_PRE_ADGE";
  const slotEmpty = isOrderLineVideoSlotEmpty(video);
  const defaultDeleteDisposition: VideoDeleteDisposition = "remove_video";
  const [deleteDisposition, setDeleteDisposition] =
    useState<VideoDeleteDisposition>(defaultDeleteDisposition);
  const deletePending = deleteVideo.isPending || cancelVideo.isPending;

  return (
    <li
      id={`admin-video-${script.id}-${videoKey}`}
      role="row"
      aria-level={3}
      aria-rowindex={rowIndex}
      data-admin-video-row
      data-tree-row
      className={cn(
        ADMIN_ORDERS_GRID_CLASS,
        ADMIN_ORDERS_ROW_PADDING_CLASS,
        "relative min-h-11 scroll-mt-20 py-1.5",
        slotEmpty
          ? "bg-muted/35 text-muted-foreground"
          : matchesStatusFilter
            ? "bg-primary-subtle/60"
            : "bg-muted/10",
      )}
      onClick={(event) => event.stopPropagation()}
    >
      <span aria-hidden role="gridcell" />
      <span aria-hidden role="gridcell" />
      <span
        aria-hidden
        className="absolute bottom-0 left-[3.85rem] top-0 w-px bg-border"
      />
      <span
        aria-hidden
        className="absolute left-[3.85rem] top-1/2 h-px w-5 bg-border"
      />
      <span
        aria-hidden
        className="absolute bottom-0 left-[5.35rem] top-0 w-px bg-border"
      />
      <span
        aria-hidden
        className="absolute left-[5.35rem] top-1/2 h-px w-4 bg-border"
      />

      <div role="gridcell" className="min-w-0 pl-14">
        <OrderLineVideoLabelEditor
          video={video}
          isPending={updateLabel.isPending}
          onSave={(nextLabel) => {
            if (!video.id) return;
            updateLabel.mutate({
              orderId: order.id,
              orderScriptId: script.id,
              deliverableId: video.id,
              label: nextLabel,
              admin: true,
            });
          }}
        />
        {video.revisionNote?.trim() ? (
          <p className="mt-0.5 line-clamp-2 text-[11px] leading-4 text-warning-subtle-foreground">
            {video.revisionNote}
          </p>
        ) : null}
      </div>

      <span role="gridcell" aria-hidden className={ADMIN_ORDERS_CELL_PARTNER_CLASS} />
      <span role="gridcell" aria-hidden className={ADMIN_ORDERS_CELL_TYPE_CLASS} />
      <span
        role="gridcell"
        aria-hidden
        className={cn(
          ADMIN_ORDERS_CELL_SCRIPTS_CLASS,
          "text-xs text-muted-foreground/45",
        )}
      >
        —
      </span>

      <span
        role="gridcell"
        className={ADMIN_ORDERS_CELL_STATUS_CLASS}
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
      >
        {isOrderLineVideoSlotEmpty(video) ? (
          <Badge
            variant="secondary"
            className="border-border/60 bg-muted/50 font-normal text-muted-foreground"
          >
            {t("marketplace.admin.orders.awaitingUpload", "Awaiting upload")}
          </Badge>
        ) : statusMenuValue && canEditStatus ? (
          <StatusMenu
            value={statusMenuValue}
            options={statusOptions}
            editable={canEditStatus}
            pending={pending}
            ariaLabel={t("marketplace.orders.list.videoStatusMenuLabel", {
              label,
              defaultValue: "Status for {{label}}",
            })}
            onChange={onStatusChange}
          />
        ) : statusMenuValue ? (
          <StatusPill status={statusMenuValue} viewer="ops" />
        ) : null}
      </span>

      <span role="gridcell" aria-hidden className={ADMIN_ORDERS_CELL_SENT_CLASS} />
      <span role="gridcell" aria-hidden className={ADMIN_ORDERS_CELL_DUE_CLASS} />

      <span
        role="gridcell"
        className={cn(ADMIN_ORDERS_CELL_ACTIONS_CLASS, "flex flex-wrap items-center gap-1")}
      >
        {video.scriptReviewId || video.preflightUrl ? (
          <ScriptReviewAction
            scriptReviewId={video.scriptReviewId}
            preflightUrl={video.preflightUrl}
            label={t("marketplace.orders.list.review", "Review")}
            ariaLabel={t("marketplace.partner.videos.viewAria", {
              label,
              title: scriptTitle,
              defaultValue: "View review for {{label}} on {{title}}",
            })}
            variant="outline"
            size="sm"
            compact
          />
        ) : null}
        {canDelete && video.id ? (
          <>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 text-xs text-destructive hover:text-destructive"
              onClick={() => {
                setDeleteDisposition(defaultDeleteDisposition);
                setDeleteOpen(true);
              }}
            >
              {t("marketplace.orders.list.deleteVideo", "Delete")}
            </Button>
            <Dialog
              open={deleteOpen}
              onOpenChange={(open) => {
                setDeleteOpen(open);
                if (!open) setDeleteDisposition(defaultDeleteDisposition);
              }}
            >
              <DialogContent onClick={(event) => event.stopPropagation()}>
                <DialogHeader>
                  <DialogTitle>
                    {slotEmpty
                      ? t(
                          "marketplace.orders.list.deleteEmptySlotTitle",
                          "Delete this video slot?",
                        )
                      : t(
                          "marketplace.orders.list.deleteVideoTitle",
                          "Archive or delete this video?",
                        )}
                  </DialogTitle>
                  <DialogDescription>
                    {slotEmpty
                      ? t(
                          "marketplace.orders.list.deleteEmptySlotDescription",
                          "This removes the empty slot from {{label}}. The script line stays in production.",
                          { label },
                        )
                      : t(
                          "marketplace.orders.list.deleteVideoDescription",
                          "What should happen to {{label}} on this script?",
                          { label },
                        )}
                  </DialogDescription>
                </DialogHeader>
                {!slotEmpty && canCancelVideo ? (
                  <fieldset className="flex flex-col gap-2 border-0 p-0">
                    <legend className="sr-only">
                      {t(
                        "marketplace.orders.list.deleteVideoChoiceLegend",
                        "Choose an action",
                      )}
                    </legend>
                    <label className="flex cursor-pointer gap-2 rounded-md border border-border px-3 py-2 text-xs">
                      <input
                        type="radio"
                        name={`delete-video-${video.id}`}
                        className="mt-0.5"
                        checked={deleteDisposition === "remove_video"}
                        onChange={() => setDeleteDisposition("remove_video")}
                      />
                      <span>
                        {t(
                          "marketplace.orders.list.deleteVideoRemoveOnly",
                          "Delete this video slot — remove it from the database and keep the script line in production.",
                        )}
                      </span>
                    </label>
                    <label className="flex cursor-pointer gap-2 rounded-md border border-border px-3 py-2 text-xs">
                      <input
                        type="radio"
                        name={`delete-video-${video.id}`}
                        className="mt-0.5"
                        checked={deleteDisposition === "cancel_video"}
                        onChange={() => setDeleteDisposition("cancel_video")}
                      />
                      <span>
                        {t(
                          "marketplace.orders.list.deleteVideoCancelSlot",
                          "Cancel this video only — mark {{label}} as Cancelled and keep the script line in production.",
                          { label },
                        )}
                      </span>
                    </label>
                  </fieldset>
                ) : null}
                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={deletePending}
                    onClick={() => setDeleteOpen(false)}
                  >
                    {t("common.cancel", "Cancel")}
                  </Button>
                  <Button
                    type="button"
                    variant="destructive"
                    disabled={deletePending}
                    onClick={() => {
                      if (
                        !slotEmpty &&
                        canCancelVideo &&
                        deleteDisposition === "cancel_video" &&
                        video.id
                      ) {
                        cancelVideo.mutate(
                          {
                            orderId: order.id,
                            orderScriptId: script.id,
                            tenantId: order.tenantId,
                            fulfillmentStatus: "CANCELLED",
                            deliverableId: video.id,
                            admin: true,
                          },
                          { onSuccess: () => setDeleteOpen(false) },
                        );
                        return;
                      }
                      deleteVideo.mutate(
                        {
                          orderId: order.id,
                          orderScriptId: script.id,
                          deliverableId: video.id!,
                        },
                        { onSuccess: () => setDeleteOpen(false) },
                      );
                    }}
                  >
                    {!slotEmpty &&
                    canCancelVideo &&
                    deleteDisposition === "cancel_video"
                      ? t(
                          "marketplace.orders.list.deleteVideoConfirmCancel",
                          "Cancel video",
                        )
                      : t(
                          "marketplace.orders.list.deleteVideoConfirm",
                          "Delete video slot",
                        )}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </>
        ) : null}
      </span>
    </li>
  );
}
